import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { showUpgradeRequired } from '../components/UpgradeRequiredDialog';
import './FinancePage.css';
import ChartOfAccountsDetailPage from './ChartOfAccountsDetailPage';
import JournalEntriesDetailPage from './JournalEntriesDetailPage';
import ExpensesDetailPage from './ExpensesDetailPage';
import FinancialReportsDetailPage from './FinancialReportsDetailPage';
import FiscalPeriodManager from '../components/FiscalPeriodManager';
import AuditTrailViewer from '../components/AuditTrailViewer';
import BankReconciliationManager from '../components/BankReconciliationManager';
import AccountHierarchyBuilder from '../components/AccountHierarchyBuilder';
import SubLedgerReconciliationManager from '../components/SubLedgerReconciliationManager';
import AccountingIntegrationsManager from '../components/AccountingIntegrationsManager';
import api from '../utils/api';
import {
  ChartBar, FileText, NotePencil, CurrencyCircleDollar, TrendUp, CalendarBlank,
  MagnifyingGlass, Bank, ClipboardText, Tree, Money, CreditCard, TrendDown, Plugs, Lock,
} from '@phosphor-icons/react';

const VALID_TABS = ['dashboard', 'coa', 'journal', 'expenses', 'reports', 'periods', 'audit', 'bank-recon', 'subledger', 'hierarchy', 'integrations'];

// Tabs gated behind a plan tier (services/plan_gating.py on the backend —
// this is UI-only, the real enforcement is each gated router's own 403).
// periods/audit/subledger/hierarchy aren't gated, so they're left out of
// this map deliberately.
const GATED_TABS = {
  coa: 'finance_advanced',
  journal: 'finance_advanced',
  expenses: 'finance_advanced',
  reports: 'finance_advanced',
  'bank-recon': 'finance_advanced',
  // AccountingIntegrationsManager (Tally/QuickBooks export/sync) —
  // routers/accounting_integrations.py, gated under fees_advanced.
  integrations: 'fees_advanced',
};

const FinancePage = () => {
  const [searchParams] = useSearchParams();
  const { hasModule } = useAuth();
  const isTabLocked = (tab) => Boolean(GATED_TABS[tab]) && !hasModule(GATED_TABS[tab]);
  const requestedTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'dashboard';
  const initialTab = isTabLocked(requestedTab) ? 'dashboard' : requestedTab;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [journalAutoOpen, setJournalAutoOpen] = useState(false);
  const [expenseAutoOpen, setExpenseAutoOpen] = useState(false);
  const [reportsInitial, setReportsInitial] = useState('trial-balance');

  const goToTab = (tab) => {
    if (isTabLocked(tab)) {
      showUpgradeRequired();
      return;
    }
    setActiveTab(tab);
    setJournalAutoOpen(false);
    setExpenseAutoOpen(false);
  };

  const runQuickAction = (action) => {
    switch (action) {
      case 'create-journal-entry':
        setJournalAutoOpen(true);
        setActiveTab('journal');
        break;
      case 'submit-expense':
        setExpenseAutoOpen(true);
        setActiveTab('expenses');
        break;
      case 'trial-balance':
        setReportsInitial('trial-balance');
        setActiveTab('reports');
        break;
      case 'profit-loss':
        setReportsInitial('profit-loss');
        setActiveTab('reports');
        break;
      case 'balance-sheet':
        setReportsInitial('balance-sheet');
        setActiveTab('reports');
        break;
      case 'bank-reconciliation':
        setActiveTab('bank-recon');
        break;
      default:
        break;
    }
  };

  const pageContent = (
    <div className="page finance-page">
      <div className="page-header">
        <div>
          <h1>Finance Module</h1>
          <p>Manage accounting, expenses, and financial reports</p>
        </div>
      </div>

      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => goToTab('dashboard')}
          title="Dashboard"
        >
          <ChartBar size={18} /> Dashboard
        </button>
        <button
          className={`tab-btn ${activeTab === 'coa' ? 'active' : ''} ${isTabLocked('coa') ? 'tab-btn--locked' : ''}`}
          onClick={() => goToTab('coa')}
          title="Chart of Accounts"
        >
          <FileText size={18} /> Chart of Accounts {isTabLocked('coa') && <Lock size={14} weight="fill" />}
        </button>
        <button
          className={`tab-btn ${activeTab === 'journal' ? 'active' : ''} ${isTabLocked('journal') ? 'tab-btn--locked' : ''}`}
          onClick={() => goToTab('journal')}
          title="Journal Entries"
        >
          <NotePencil size={18} /> Journal Entries {isTabLocked('journal') && <Lock size={14} weight="fill" />}
        </button>
        <button
          className={`tab-btn ${activeTab === 'expenses' ? 'active' : ''} ${isTabLocked('expenses') ? 'tab-btn--locked' : ''}`}
          onClick={() => goToTab('expenses')}
          title="Expenses"
        >
          <CurrencyCircleDollar size={18} /> Expenses {isTabLocked('expenses') && <Lock size={14} weight="fill" />}
        </button>
        <button
          className={`tab-btn ${activeTab === 'reports' ? 'active' : ''} ${isTabLocked('reports') ? 'tab-btn--locked' : ''}`}
          onClick={() => goToTab('reports')}
          title="Reports"
        >
          <TrendUp size={18} /> Reports {isTabLocked('reports') && <Lock size={14} weight="fill" />}
        </button>
        <button
          className={`tab-btn ${activeTab === 'periods' ? 'active' : ''}`}
          onClick={() => goToTab('periods')}
          title="Fiscal Periods"
        >
          <CalendarBlank size={18} /> Fiscal Periods
        </button>
        <button
          className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
          onClick={() => goToTab('audit')}
          title="Audit Trail"
        >
          <MagnifyingGlass size={18} /> Audit Trail
        </button>
        <button
          className={`tab-btn ${activeTab === 'bank-recon' ? 'active' : ''} ${isTabLocked('bank-recon') ? 'tab-btn--locked' : ''}`}
          onClick={() => goToTab('bank-recon')}
          title="Bank Reconciliation"
        >
          <Bank size={18} /> Bank Recon {isTabLocked('bank-recon') && <Lock size={14} weight="fill" />}
        </button>
        <button
          className={`tab-btn ${activeTab === 'subledger' ? 'active' : ''}`}
          onClick={() => goToTab('subledger')}
          title="Sub-Ledger Reconciliation"
        >
          <ClipboardText size={18} /> Sub-Ledger
        </button>
        <button
          className={`tab-btn ${activeTab === 'hierarchy' ? 'active' : ''}`}
          onClick={() => goToTab('hierarchy')}
          title="Account Hierarchy"
        >
          <Tree size={18} /> Hierarchy
        </button>
        <button
          className={`tab-btn ${activeTab === 'integrations' ? 'active' : ''} ${isTabLocked('integrations') ? 'tab-btn--locked' : ''}`}
          onClick={() => goToTab('integrations')}
          title="Accounting Integrations"
        >
          <Plugs size={18} /> Integrations {isTabLocked('integrations') && <Lock size={14} weight="fill" />}
        </button>
      </div>

      <div className="page-content">
        {activeTab === 'dashboard' && <FinanceDashboard onQuickAction={runQuickAction} />}
        {activeTab === 'coa' && <ChartOfAccountsDetailPage />}
        {activeTab === 'journal' && <JournalEntriesDetailPage autoOpenCreate={journalAutoOpen} />}
        {activeTab === 'expenses' && <ExpensesDetailPage autoOpenCreate={expenseAutoOpen} />}
        {activeTab === 'reports' && <FinancialReportsDetailPage initialReport={reportsInitial} />}
        {activeTab === 'periods' && <FiscalPeriodManager />}
        {activeTab === 'audit' && <AuditTrailViewer />}
        {activeTab === 'bank-recon' && <BankReconciliationManager />}
        {activeTab === 'subledger' && <SubLedgerReconciliationManager />}
        {activeTab === 'hierarchy' && <AccountHierarchyBuilder />}
        {activeTab === 'integrations' && <AccountingIntegrationsManager />}
      </div>
    </div>
  );

  return <Layout>{pageContent}</Layout>;
};

// Dashboard Component
const FinanceDashboard = ({ onQuickAction }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [financialHealth, setFinancialHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard metrics
      const metricsData = await api.get('/api/finance/dashboard-metrics');

      // Fetch recent transactions
      const transactionsData = await api.get('/api/finance/recent-transactions', {
        params: { limit: 4 }
      });

      // Fetch financial health indicators
      const healthData = await api.get('/api/finance/health-indicators');

      setDashboardData(metricsData);
      setRecentTransactions(transactionsData.transactions || []);
      setFinancialHealth(healthData);
      setError(null);
    } catch (err) {
      console.error('Error fetching financial data:', err);
      setError('Failed to load financial data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="finance-dashboard">
        <h2>Financial Overview</h2>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading financial data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="finance-dashboard">
        <h2>Financial Overview</h2>
        <div style={{ color: '#d32f2f', padding: '20px', backgroundColor: '#ffebee', borderRadius: '8px' }}>
          <p>{error}</p>
          <button onClick={fetchFinancialData} style={{ marginTop: '10px', padding: '8px 16px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="finance-dashboard">
        <h2>Financial Overview</h2>
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="finance-dashboard">
      <h2>Financial Overview</h2>
      
      <div className="dashboard-metrics">
        <div className="metric-card">
          <div className="metric-icon"><Money size={28} /></div>
          <div className="metric-content">
            <h3>Cash Balance</h3>
            <p className="metric-value">{formatCurrency(dashboardData.cashBalance)}</p>
            <p className="metric-label">Business Checking Account</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon"><CreditCard size={28} /></div>
          <div className="metric-content">
            <h3>Accounts Receivable</h3>
            <p className="metric-value">{formatCurrency(dashboardData.accountsReceivable)}</p>
            <p className="metric-label">Outstanding student fees</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon"><ChartBar size={28} /></div>
          <div className="metric-content">
            <h3>Total Assets</h3>
            <p className="metric-value">{formatCurrency(dashboardData.totalAssets)}</p>
            <p className="metric-label">All assets (cash + fixed + receivables)</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon"><TrendDown size={28} /></div>
          <div className="metric-content">
            <h3>Accounts Payable</h3>
            <p className="metric-value">{formatCurrency(dashboardData.accountsPayable)}</p>
            <p className="metric-label">Current liabilities</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon"><TrendUp size={28} /></div>
          <div className="metric-content">
            <h3>Net Profit (YTD)</h3>
            <p className="metric-value">{formatCurrency(dashboardData.netProfit)}</p>
            <p className="metric-label">Revenue - Expenses (this fiscal year)</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon"><ChartBar size={28} weight="duotone" /></div>
          <div className="metric-content">
            <h3>Profit Margin</h3>
            <p className="metric-value">{dashboardData.profitMargin}%</p>
            <p className="metric-label">Net profit ÷ Total revenue</p>
          </div>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="revenue-source">
          <h3>Revenue by Source (YTD)</h3>
          <div className="revenue-breakdown">
            {dashboardData.revenueBySource && dashboardData.revenueBySource.map((source, idx) => (
              <div key={idx} className="revenue-item">
                <span className="revenue-label">{source.name}</span>
                <span className="revenue-bar" style={{width: `${source.percentage}%`}}></span>
                <span className="revenue-amount">{formatCurrency(source.amount)} ({source.percentage}%)</span>
              </div>
            ))}
          </div>
        </div>

        <div className="section-wrapper">
          <div className="recent-section">
            <h3>Recent Transactions</h3>
            <div className="transaction-list">
              {recentTransactions.length > 0 ? (
                recentTransactions.map((transaction, idx) => (
                  <div key={idx} className="transaction-item">
                    <span className="trans-date">{formatDate(transaction.date)}</span>
                    <span className="trans-desc">{transaction.description}</span>
                    <span className="trans-amount" style={{color: transaction.amount < 0 ? '#d32f2f' : '#2e7d32'}}>
                      {transaction.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(transaction.amount))}
                    </span>
                  </div>
                ))
              ) : (
                <p style={{color: '#999', fontSize: '14px'}}>No transactions available</p>
              )}
            </div>
          </div>

          <div className="quick-actions">
            <h3>Quick Actions</h3>
            <button className="action-button" onClick={() => onQuickAction('create-journal-entry')}>Create Journal Entry</button>
            <button className="action-button" onClick={() => onQuickAction('submit-expense')}>Submit Expense</button>
            <button className="action-button" onClick={() => onQuickAction('trial-balance')}>View Trial Balance</button>
            <button className="action-button" onClick={() => onQuickAction('profit-loss')}>Generate P&L</button>
            <button className="action-button" onClick={() => onQuickAction('balance-sheet')}>View Balance Sheet</button>
            <button className="action-button" onClick={() => onQuickAction('bank-reconciliation')}>Bank Reconciliation</button>
          </div>
        </div>
      </div>

      {financialHealth && (
        <div className="financial-health">
          <h3>Financial Health Indicators</h3>
          <div className="health-metrics">
            {financialHealth.indicators && financialHealth.indicators.map((indicator, idx) => (
              <div key={idx} className="health-item">
                <span className="health-label">{indicator.label}</span>
                <span className="health-value">{indicator.value}</span>
                <span className="health-status">{indicator.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper functions
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'GHS 0';
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

export default FinancePage;
